import { useParams, useNavigate } from 'react-router-dom';
import { useVendorAccess } from '@/hooks/useVendorAccess';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getProductById, saveDraftChanges, submitForReview } from '@/data/vendorDashboard';
import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Save, Send, Plus, X } from 'lucide-react';
import { DraftData } from '@/contracts/vendor-dashboard';
import { VendorImageUpload } from '@/components/vendor/VendorImageUpload';

export default function VendorEditListing() {
  useVendorAccess();
  const { productId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const { data: product, isLoading } = useQuery({
    queryKey: ['product', productId],
    queryFn: () => getProductById(productId!),
    enabled: !!productId,
  });
  
  // Verify ownership
  useEffect(() => {
    if (product && product.vendor_email !== user?.email) {
      toast.error('You do not have permission to edit this product');
      navigate('/vendor');
    }
  }, [product, user, navigate]);
  
  // Local state for edits
  const [draftFields, setDraftFields] = useState<DraftData>({
    title: '',
    subtitle: '',
    description: '',
    booking_url: '',
    best_for: [],
    feature_highlights: [],
    categories: [],
    image: '',
    profile_image: '',
  });
  
  useEffect(() => {
    if (product) {
      const draft = product.draft_data as DraftData | null;
      setDraftFields({
        title: draft?.title ?? product.title ?? '',
        subtitle: draft?.subtitle ?? product.subtitle ?? '',
        description: draft?.description ?? product.description ?? '',
        booking_url: draft?.booking_url ?? product.booking_url ?? '',
        best_for: draft?.best_for ?? (product.best_for as string[]) ?? [],
        feature_highlights: draft?.feature_highlights ?? (product.feature_highlights as any[]) ?? [],
        categories: draft?.categories ?? (product.categories as string[]) ?? [],
        image: draft?.image ?? product.image ?? '',
        profile_image: draft?.profile_image ?? product.profile_image ?? '',
      });
    }
  }, [product]);
  
  const saveDraftMutation = useMutation({
    mutationFn: () => saveDraftChanges(productId!, draftFields),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product', productId] });
      toast.success('Draft saved successfully');
    },
    onError: (error: any) => {
      toast.error('Failed to save draft: ' + error.message);
    },
  });
  
  const submitMutation = useMutation({
    mutationFn: async () => {
      await saveDraftChanges(productId!, draftFields);
      await submitForReview(productId!);
    },
    onSuccess: () => {
      toast.success('Changes submitted for review');
      navigate('/vendor');
    },
    onError: (error: any) => {
      toast.error('Failed to submit: ' + error.message);
    },
  });
  
  if (isLoading || !product) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 container py-8">
          <div>Loading...</div>
        </main>
        <Footer />
      </div>
    );
  }
  
  // Preview overlay: merge live + draft
  const previewData = {
    ...product,
    ...draftFields,
  };
  
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 container py-8">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/vendor')}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">Edit Listing</h1>
          <div className="flex items-center gap-2">
            <Badge variant="outline">{product.status}</Badge>
            {product.draft_data && (
              <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                Has pending changes
              </Badge>
            )}
          </div>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-8">
          {/* LEFT: Live Preview */}
          <div>
            <h2 className="text-xl font-bold mb-4">Preview</h2>
            <Card className="p-6 sticky top-24">
              <img 
                src={previewData.image || '/placeholder.svg'} 
                alt={previewData.title}
                className="w-full h-64 object-cover rounded-lg mb-4"
              />
              {previewData.profile_image && (
                <img 
                  src={previewData.profile_image} 
                  alt="Profile"
                  className="w-16 h-16 rounded-full border-2 border-white shadow-lg mb-4"
                />
              )}
              <h3 className="text-2xl font-bold mb-2">{previewData.title}</h3>
              <p className="text-muted-foreground mb-4">{previewData.subtitle}</p>
              <Separator className="my-4" />
              <div className="prose prose-sm max-w-none">
                <p className="whitespace-pre-wrap">{previewData.description}</p>
              </div>
              {previewData.booking_url && (
                <>
                  <Separator className="my-4" />
                  <Button className="w-full" asChild>
                    <a href={previewData.booking_url} target="_blank" rel="noopener noreferrer">
                      Book Now
                    </a>
                  </Button>
                </>
              )}
            </Card>
          </div>
          
          {/* RIGHT: Edit Form */}
          <div>
            <h2 className="text-xl font-bold mb-4">Edit Fields</h2>
            <Card className="p-6">
              <div className="space-y-6">
                {(product.draft_data as any)?.rejection_reason && (
                  <Alert variant="destructive">
                    <AlertDescription>
                      <strong>Previous submission rejected:</strong> {(product.draft_data as any).rejection_reason}
                    </AlertDescription>
                  </Alert>
                )}
                
                <div>
                  <Label htmlFor="title">Title</Label>
                  <Input 
                    id="title"
                    value={draftFields.title}
                    onChange={(e) => setDraftFields(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Product title"
                  />
                </div>
                
                <div>
                  <Label htmlFor="subtitle">Subtitle</Label>
                  <Input 
                    id="subtitle"
                    value={draftFields.subtitle}
                    onChange={(e) => setDraftFields(prev => ({ ...prev, subtitle: e.target.value }))}
                    placeholder="Short description"
                  />
                </div>
                
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea 
                    id="description"
                    value={draftFields.description}
                    onChange={(e) => setDraftFields(prev => ({ ...prev, description: e.target.value }))}
                    rows={10}
                    placeholder="Detailed product description"
                  />
                </div>
                
                <div>
                  <Label htmlFor="booking_url">Booking URL</Label>
                  <Input 
                    id="booking_url"
                    type="url"
                    value={draftFields.booking_url}
                    onChange={(e) => setDraftFields(prev => ({ ...prev, booking_url: e.target.value }))}
                    placeholder="https://..."
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    The URL where customers can book or purchase your service
                  </p>
                </div>
                
                <div>
                  <Label>Best For</Label>
                  <div className="space-y-2 mt-2">
                    {['new-agents', 'growing-agents', 'top-producers', 'team-leaders-brokers'].map((opt) => (
                      <div key={opt} className="flex items-center space-x-2">
                        <Checkbox
                          id={opt}
                          checked={draftFields.best_for?.includes(opt)}
                          onCheckedChange={(checked) => {
                            const current = draftFields.best_for || [];
                            setDraftFields({
                              ...draftFields,
                              best_for: checked
                                ? [...current, opt]
                                : current.filter(x => x !== opt)
                            });
                          }}
                        />
                        <Label htmlFor={opt} className="cursor-pointer capitalize">
                          {opt.replace(/-/g, ' ')}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Feature Highlights</Label>
                  <div className="space-y-2 mt-2">
                    {draftFields.feature_highlights?.map((fh, idx) => (
                      <div key={idx} className="flex gap-2 items-start border p-2 rounded">
                        <Input
                          placeholder="Icon"
                          value={fh.icon}
                          onChange={(e) => {
                            const updated = [...(draftFields.feature_highlights || [])];
                            updated[idx].icon = e.target.value;
                            setDraftFields({ ...draftFields, feature_highlights: updated });
                          }}
                          className="w-16"
                        />
                        <Input
                          placeholder="Title"
                          value={fh.title}
                          onChange={(e) => {
                            const updated = [...(draftFields.feature_highlights || [])];
                            updated[idx].title = e.target.value;
                            setDraftFields({ ...draftFields, feature_highlights: updated });
                          }}
                        />
                        <Input
                          placeholder="Description"
                          value={fh.description}
                          onChange={(e) => {
                            const updated = [...(draftFields.feature_highlights || [])];
                            updated[idx].description = e.target.value;
                            setDraftFields({ ...draftFields, feature_highlights: updated });
                          }}
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            const updated = draftFields.feature_highlights?.filter((_, i) => i !== idx);
                            setDraftFields({ ...draftFields, feature_highlights: updated });
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setDraftFields({
                          ...draftFields,
                          feature_highlights: [
                            ...(draftFields.feature_highlights || []),
                            { icon: '✓', title: '', description: '' }
                          ]
                        });
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Feature Highlight
                    </Button>
                  </div>
                </div>

                <div>
                  <Label>Categories</Label>
                  <div className="space-y-2 mt-2">
                    {['crm', 'marketing', 'lead-generation', 'transaction-management', 'coaching'].map((cat) => (
                      <div key={cat} className="flex items-center space-x-2">
                        <Checkbox
                          id={cat}
                          checked={draftFields.categories?.includes(cat)}
                          onCheckedChange={(checked) => {
                            const current = draftFields.categories || [];
                            setDraftFields({
                              ...draftFields,
                              categories: checked
                                ? [...current, cat]
                                : current.filter(x => x !== cat)
                            });
                          }}
                        />
                        <Label htmlFor={cat} className="cursor-pointer capitalize">
                          {cat.replace(/-/g, ' ')}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <VendorImageUpload
                    label="Main Product Image"
                    currentImage={draftFields.image}
                    onImageChange={(url) => setDraftFields(prev => ({ ...prev, image: url }))}
                    folder="products"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Recommended: 1200x675px (16:9 ratio)
                  </p>
                </div>

                <div>
                  <VendorImageUpload
                    label="Profile Image"
                    currentImage={draftFields.profile_image}
                    onImageChange={(url) => setDraftFields(prev => ({ ...prev, profile_image: url }))}
                    folder="profiles"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Recommended: Square format (e.g., 400x400px)
                  </p>
                </div>
                
                <Separator />
                
                <div className="flex gap-3">
                  <Button 
                    variant="outline"
                    onClick={() => saveDraftMutation.mutate()}
                    disabled={saveDraftMutation.isPending || submitMutation.isPending}
                    className="flex-1"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save Draft
                  </Button>
                  <Button 
                    onClick={() => submitMutation.mutate()}
                    disabled={saveDraftMutation.isPending || submitMutation.isPending}
                    className="flex-1"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Submit for Review
                  </Button>
                </div>
                
                <p className="text-xs text-muted-foreground">
                  <strong>Save Draft:</strong> Saves your changes without submitting for review. <br />
                  <strong>Submit for Review:</strong> Sends your changes to the admin team for approval.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
